
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { GetStaticProps } from 'next';
import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import clsxm from '@/lib/clsxm';
import Footer from '@/components/layout/Footer';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import Seo from '@/components/Seo';
import { getAllPostsForGuide } from '@/utils/wordpress';
interface Article {
    title: string;
    slug: string;
    description: string;
    date: string;
    featuredImage: string;
    author: any;
    excerpt: string;
    tags: {
        edges: Array<{
            node: {
                name: string;
            };
        }>;
    };
}
interface BlogProps {
    articles: any[];
}
const Blog = ({ articles }: BlogProps) => {
    const [activeTag, setActiveTag] = React.useState<string>('');
    // Group articles by tag
    const articlesByTag = React.useMemo(() => {
        const grouped: Record<string, any[]> = {};
        articles.forEach(({ node }: any) => {
            if (node.tags?.edges?.length > 0) {
                // Add article to each of its tags
                node.tags.edges.forEach(({ node: tag }: any) => {
                    const tagName = tag.name;
                    if (!grouped[tagName]) {
                        grouped[tagName] = [];
                    }
                    grouped[tagName].push({ node });
                });
            }
            else {
                // Articles without tags go to "Other" category
                if (!grouped['Other']) {
                    grouped['Other'] = [];
                }
                grouped['Other'].push({ node });
            }
        });
        // Sort tags alphabetically, with "Other" at the end
        const sortedTags = Object.keys(grouped).sort((a, b) => {
            if (a === 'Other')
                return 1;
            if (b === 'Other')
                return -1;
            return a.localeCompare(b);
        });
        return sortedTags.map((tag) => ({
            tag,
            articles: grouped[tag],
        }));
    }, [articles]);
    const scrollToSection = (tag: string) => {
        setActiveTag(tag);
        const element = document.getElementById(`section-${tag}`);
        if (element) {
            const offset = 100; // Account for sticky header
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;
            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth',
            });
        }
    };
    // Track active section on scroll
    React.useEffect(() => {
        const handleScroll = () => {
            const sections = articlesByTag.map(({ tag }) => ({
                tag,
                element: document.getElementById(`section-${tag}`),
            }));
            const scrollPosition = window.scrollY + 150; // Offset for sticky header
            for (const section of sections) {
                if (section.element) {
                    const { offsetTop, offsetHeight } = section.element;
                    if (scrollPosition >= offsetTop &&
                        scrollPosition < offsetTop + offsetHeight) {
                        setActiveTag(section.tag);
                        break;
                    }
                }
            }
        };
        window.addEventListener('scroll', handleScroll);
        handleScroll(); // Initial check
        return () => window.removeEventListener('scroll', handleScroll);
    }, [articlesByTag]);
    return (<main className='dark:bg-slate-900'>
      <Seo templateTitle='Guide'/>
      <div className='relative '>
        <div className='mx-auto'>
          <div className='z-1 relative pb-8 sm:pb-16 md:pb-20 lg:w-full'>
            <LandingPageNavigation />
          </div>
        </div>
      </div>
      <div className='relative bg-gray-50 dark:bg-slate-700 px-4 pt-16 pb-20 sm:px-6 lg:px-8 lg:pt-24 lg:pb-28'>
        <div className='absolute inset-0'>
          <div className='h-1/3 bg-white dark:bg-slate-900 sm:h-2/3'/>
        </div>
        <section className='relative mx-auto max-w-7xl'>
          <div className='text-center'>
            <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
              Guide
            </h2>
            <p className='mx-auto mt-3 max-w-2xl text-xl text-gray-500 sm:mt-4'>
              Learn how to use our AI tools to automate your content creation,
              customer support, and more.
            </p>
          </div>

          {/* Tag Navigation */}
          <div className='sticky top-0 z-10 bg-white dark:bg-slate-800 shadow-sm mt-8 -mx-4 sm:-mx-6 lg:-mx-8 px-1 sm:px-6 lg:px-8 py-1'>
            <div className='flex flex-wrap gap-2 justify-center max-w-5xl mx-auto'>
              {articlesByTag.map(({ tag, articles: tagArticles }) => {
            const isActive = activeTag === tag;
            return (<button key={tag} onClick={() => scrollToSection(tag)} className={clsxm('inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all duration-200', isActive
                    ? 'bg-indigo-600 text-white shadow-md transform scale-105'
                    : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 hover:bg-indigo-100 dark:hover:bg-indigo-900 hover:text-indigo-700 dark:hover:text-indigo-200')}>
                    {tag}
                    <span className={clsxm('ml-1.5 sm:ml-2 inline-flex items-center justify-center px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-xs font-bold rounded-full', isActive
                    ? 'bg-indigo-500 text-white'
                    : 'bg-gray-200 dark:bg-slate-600 text-gray-600 dark:text-slate-300')}>
                      {tagArticles.length}
                    </span>
                  </button>);
        })}
            </div>
          </div>
          <div className='mx-auto mt-12 space-y-16'>
            {articlesByTag.map(({ tag, articles: tagArticles }) => (<div key={tag} id={`section-${tag}`} className='relative'>
                <div className='flex items-center mb-8'>
                  <h3 className='text-2xl font-bold text-gray-900 dark:text-slate-200'>
                    {tag}
                  </h3>
                  <span className='ml-3 inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200'>
                    {tagArticles.length}{' '}
                    {tagArticles.length === 1 ? 'article' : 'articles'}
                  </span>
                </div>
                <div className='grid max-w-lg gap-5 lg:max-w-none lg:grid-cols-3'>
                  {tagArticles.map(({ node }) => (<PostPreview key={node.slug} title={node.title} coverImage={node.featuredImage} date={node.date} author={node.author} slug={node.slug} excerpt={node.excerpt}/>))}
                </div>
                {tag !== articlesByTag[articlesByTag.length - 1].tag && (<div className='mt-12 border-b border-gray-200 dark:border-gray-700'/>)}
              </div>))}
          </div>
        </section>
      </div>
      <Footer />
    </main>);
};
export function PostPreview({ title, coverImage, date, excerpt, author, slug, }: any) {
    return (<div className='flex flex-col overflow-hidden rounded-lg shadow-lg'>
      <div className='flex-shrink-0'>
        <Link href={`/guide/${slug}`}>
          <Image className='h-48 w-full object-cover' src={coverImage?.node.sourceUrl} alt={title} width={500} height={500}/>
        </Link>
      </div>
      {/* <div className='mb-5'>
          {coverImage && (
            <CoverImage title={title} coverImage={coverImage} slug={slug} />
          )}
        </div> */}
      <div className='flex flex-1 flex-col justify-between bg-white dark:bg-slate-800 p-6'>
        <div className='flex-1'>
          <Link href={`/guide/${slug}`} className='mt-2 block'>
            <p className='text-xl font-semibold text-gray-900 dark:text-slate-100' dangerouslySetInnerHTML={{ __html: title }}></p>
            <p className='mt-3 text-base text-gray-500 dark:text-slate-300' dangerouslySetInnerHTML={{ __html: excerpt }}></p>
          </Link>
        </div>
      </div>
      {/* <h3 className='text-3xl mb-3 leading-snug'>
          <Link
            href={`/guide/${slug}`}
            className='hover:underline'
            dangerouslySetInnerHTML={{ __html: title }}
          ></Link>
        </h3>
        <div className='text-lg mb-4'>{formatDate(date)}</div>
        <div
          className='text-lg leading-relaxed mb-4'
          dangerouslySetInnerHTML={{ __html: excerpt }}
        /> */}
      {/* <Avatar author={author} /> */}
    </div>);
}
export function Avatar({ author }: any) {
    const isAuthorHaveFullName = author?.node?.firstName && author?.node?.lastName;
    const name = isAuthorHaveFullName
        ? `${author.node.firstName} ${author.node.lastName}`
        : author?.node?.name || null;
    if (!author) {
        return null;
    }
    console.log(author);
    return (<div className='flex items-center mt-3'>
      <div className='w-10 h-10 relative mr-4'>
        <img src={author.node.avatar.url} className='rounded-full' alt={name}/>
      </div>
      <div className='text-sm font-medium'>{name}</div>
    </div>);
}
interface CoverImageProps {
    title: string;
    coverImage: {
        node: {
            sourceUrl: string;
        };
    };
    slug?: string;
}
export function CoverImage({ title, coverImage, slug }: CoverImageProps) {
    const image = (<Image width={2000} height={1000} alt={`Cover Image for ${title}`} src={coverImage?.node.sourceUrl} className={clsxm('shadow-small', {
            'hover:shadow-medium transition-shadow duration-200': slug,
        })}/>);
    return (<div className='sm:mx-0'>
      {slug ? (<Link href={`/guide/${slug}`} aria-label={title}>
          {image}
        </Link>) : (image)}
    </div>);
}
export default Blog;
const getStaticProps: GetStaticProps = async ({ preview = false, locale, }) => {
    const allPosts = await getAllPostsForGuide(preview);
    //redirect 301 to the /blog
    // if (locale !== 'en') {
    //   return {
    //     redirect: {
    //       destination: `/blog`,
    //       permanent: true,
    //     },
    //   };
    // }
    return {
        props: { articles: allPosts['edges'], preview },
        revalidate: 10,
    };
};

    async function __Next_Translate__getStaticProps__1a0cbfb665e__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/guide/index',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a0cbfb665e__ as getStaticProps }
  